import React, { useEffect, useState, useMemo } from 'react';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { getAppName } from 'src/utils/app';
import _omit from 'lodash/omit';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import AtlasV2DorisColumnChart, {
  AtlasV2DorisColumnChartProps
} from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisColumnChart';
import { METRICTYPE } from 'src/utils/entityDefinitions/entityDefinitionTypes';

export interface AtlasV2DorisDoubleDropdownChartProps
  extends Omit<
    AtlasV2DorisColumnChartProps,
    'indexName' | 'fieldName' | 'metricType' | 'showMarketShare' | 'metricTypeDisplay' | 'marketShareCalculator'
  > {
  fields: {
    fieldName: string;
    label: string;
    metricType: string;
    isMarketShare?: boolean;
    metricTypeDisplay: string;
    /**
     * Optional custom function to calculate market share values
     * If provided, this will be used instead of the default market share calculation
     */
    marketShareCalculator?: (value: number, isComparison?: boolean) => number;
  }[];
  groupByFields: {
    id: string;
    label: string;
    indexName?: string;
  }[];
  showSingleGroupBy?: boolean;
  /**
   * Optionally override the selected field/metric from the parent component.
   * Defaults to the first field in the fields array.
   */
  selectedField?: string;
  selectedGroupByField?: string;
  /**
   * Whether the chart is in a loading state.
   * This should be controlled by the parent component.
   */
  isLoading?: boolean;
  /**
   * Optional custom renderers for the selectors shown in the title.
   * Use these to inject a searchable dropdown for metric or group-by selectors.
   */
  renderMetricSelector?: (args: {
    options: { id: string; label: string }[];
    selectedId: string;
    onChange: (id: string) => void;
  }) => React.ReactNode;
  renderGroupBySelector?: (args: {
    options: { id: string; label: string }[];
    selectedId: string;
    onChange: (id: string) => void;
  }) => React.ReactNode;
}

const getField = (indexName: string, fieldName: string) => {
  return INDEX_FIELDS.getField(getAppName(), indexName, fieldName);
};

/**
 * Query Doris data with two dropdowns. The first is for metric fields and
 * the second is for group by fields.
 */
export default function AtlasV2DorisDoubleDropdownChart(props: AtlasV2DorisDoubleDropdownChartProps) {
  const { fields, groupByFields, showSingleGroupBy, selectedField, selectedGroupByField, isLoading = false } = props;

  const [selectedFieldName, setSelectedFieldName] = useState(selectedField || fields[0].fieldName);
  const [selectedGroupByFieldName, setSelectedGroupByFieldName] = useState(selectedGroupByField || groupByFields[0].id);

  useEffect(() => {
    if (selectedField) {
      setSelectedFieldName(selectedField);
    }
  }, [selectedField]);

  useEffect(() => {
    if (selectedGroupByField) {
      setSelectedGroupByFieldName(selectedGroupByField);
    }
  }, [selectedGroupByField]);

  // Get the currently selected field's configuration
  const selectedFieldConfig = useMemo(() => {
    return fields.find((field) => field.fieldName === selectedFieldName) || fields[0];
  }, [fields, selectedFieldName]);

  // Get the selected group by field configuration
  const selectedGroupByFieldConfig = useMemo(() => {
    return groupByFields.find((field) => field.id === selectedGroupByFieldName) || groupByFields[0];
  }, [groupByFields, selectedGroupByFieldName]);

  // Map from group field to index name
  const groupByFieldToIndex = useMemo(() => {
    return groupByFields.reduce((acc, field) => {
      return {
        ...acc,
        [field.id]: field.indexName
      };
    }, {} as Record<string, string | undefined>);
  }, [groupByFields]);

  // Get the index name for the selected group by field
  const indexName = groupByFieldToIndex[selectedGroupByFieldName];

  // Get the actual field name to use in the query - based on the selected group by field
  const queryFieldName = useMemo(() => {
    return selectedGroupByFieldConfig.id;
  }, [selectedGroupByFieldConfig]);

  // Determine the metric type display - use PERCENT for market share metrics
  const metricTypeDisplay = useMemo(() => {
    if (selectedFieldConfig.isMarketShare) {
      return METRICTYPE.PERCENT;
    }
    // Use the field's specified metric type display or undefined (to let the chart component use its default)
    return selectedFieldConfig.metricTypeDisplay;
  }, [selectedFieldConfig]);

  // Calculate final loading state (combine parent's isExtraLoading with our isLoading)
  const combinedExtraLoading = useMemo(() => {
    return props.isExtraLoading || isLoading;
  }, [props.isExtraLoading, isLoading]);

  const metricOptions = useMemo(
    () =>
      fields.map((field) => ({
        id: field.fieldName,
        label: field.label || getField(indexName, field.fieldName).displayName
      })),
    [fields, indexName]
  );

  const groupByOptions = useMemo(() => groupByFields.map((g) => ({ id: g.id, label: g.label })), [groupByFields]);

  return (
    <AtlasV2DorisColumnChart
      {...props}
      indexName={indexName}
      fieldName={queryFieldName}
      metricType={selectedFieldConfig.metricType}
      metricTypeDisplay={metricTypeDisplay}
      showMarketShare={!!selectedFieldConfig.isMarketShare}
      marketShareCalculator={selectedFieldConfig.marketShareCalculator}
      isExtraLoading={combinedExtraLoading}
      beaconChartWithLegendPropsBuilder={(legendProps) => {
        const baseProps = _omit(legendProps, 'singleDropdownProps');

        const renderMetric = () =>
          props.renderMetricSelector ? (
            props.renderMetricSelector({
              options: metricOptions,
              selectedId: selectedFieldName,
              onChange: (id) => setSelectedFieldName(id)
            })
          ) : (
            <SlDropdownMenu
              buttonVariant="title"
              options={metricOptions}
              defaultLabel=""
              onChange={(option) => setSelectedFieldName(option.id)}
              selectedId={selectedFieldName}
            />
          );

        const renderGroupBy = () =>
          props.renderGroupBySelector ? (
            props.renderGroupBySelector({
              options: groupByOptions,
              selectedId: selectedGroupByFieldName,
              onChange: (id) => setSelectedGroupByFieldName(id)
            })
          ) : (
            <SlDropdownMenu
              buttonVariant="title"
              options={groupByFields}
              defaultLabel=""
              onChange={(option) => setSelectedGroupByFieldName(option.id)}
              selectedId={selectedGroupByFieldName}
            />
          );

        // Build title with dropdowns
        const titleComponent = showSingleGroupBy ? (
          <Flex gap="sm" alignItems="center">
            {renderMetric()}
            <Text variant="h4">by</Text>
            <Text variant="h4">{groupByFields[0]?.label ?? ''}</Text>
          </Flex>
        ) : (
          <Flex gap="sm" alignItems="center">
            {fields.length > 1 ? renderMetric() : <Text variant="h4">{selectedFieldConfig.label}</Text>}
            <Text variant="h4">by</Text>
            {groupByFields?.length > 1 ? renderGroupBy() : <Text variant="h4">{groupByFields[0]?.label ?? ''}</Text>}
          </Flex>
        );

        // If the parent component provided a beaconChartWithLegendPropsBuilder,
        // call it with our base props and include context about selections
        if (props.beaconChartWithLegendPropsBuilder) {
          const parentProps = props.beaconChartWithLegendPropsBuilder({
            ...baseProps,
            selectedField: selectedFieldConfig,
            selectedGroupByField: selectedGroupByFieldName,
            title: titleComponent
          });

          return {
            ...parentProps,
            title: titleComponent
          };
        }

        // Otherwise just return our props
        return {
          ...baseProps,
          title: titleComponent
        };
      }}
    />
  );
}
